import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound, Copy, Trash2 } from "lucide-react";

// Super-admin: API keys for external integrations (Wingman etc.). The key is shown exactly once.
export default function AdminApiKeys() {
  const [data, setData] = useState({ keys: [], emails_sent_total: 0 });
  const [name, setName] = useState("Wingman");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/external-api-keys`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/external-api-keys`, { name });
      setCreated(r.data);
      load();
    } catch (e) { toast.error("Не удалось создать ключ"); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Отозвать ключ? Интеграция, использующая его, перестанет работать.")) return;
    await axios.delete(`${API}/admin/external-api-keys/${id}`);
    load();
  };
  const copy = async (v) => { try { await navigator.clipboard.writeText(v); toast.success("Скопировано"); } catch (e) { /* ignore */ } };

  return (
    <div data-testid="admin-api-keys" className="space-y-4">
      <div className="rounded-xl border-2 border-black bg-white p-5">
        <h3 className="font-bold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5" /> API-ключи внешних интеграций</h3>
        <p className="text-sm text-gray-600 mt-1">Для серверных интеграций (Wingman): <code>POST /api/v1/email/send</code>, <code>GET /api/v1/ping</code>. Заголовок <code>X-API-Key</code>. Документация: <code>/docs/external-api.md</code> в репозитории. Писем отправлено через API: <b data-testid="admin-api-emails-total">{data.emails_sent_total}</b></p>
        <div className="flex gap-2 mt-4">
          <Input data-testid="admin-api-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Название (например, Wingman)" className="border-2 border-black max-w-xs" />
          <Button data-testid="admin-api-key-create" onClick={create} disabled={busy || !name.trim()} className="bg-black text-white hover:bg-gray-800">Создать ключ</Button>
        </div>
        {created && (
          <div data-testid="admin-api-key-created" className="mt-4 p-4 rounded-lg bg-amber-50 border-2 border-amber-400">
            <p className="font-semibold text-amber-900">Ключ «{created.name}» создан. Показывается один раз — сохраните его в секреты Wingman (например, как CHATITY_API_KEY):</p>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid="admin-api-key-value" className="flex-1 break-all bg-white border rounded px-2 py-1 text-sm">{created.key}</code>
              <Button data-testid="admin-api-key-copy" size="sm" variant="outline" onClick={() => copy(created.key)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
      <div className="rounded-xl border-2 border-black bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black text-white"><tr><th className="text-left p-3">Название</th><th className="text-left p-3">Префикс</th><th className="text-left p-3">Создан</th><th className="text-left p-3">Последнее использование</th><th className="p-3"></th></tr></thead>
          <tbody>
            {data.keys.length === 0 && <tr><td colSpan={5} className="p-4 text-gray-500 text-center">Ключей пока нет</td></tr>}
            {data.keys.map((k) => (
              <tr key={k.id} data-testid={`admin-api-key-row-${k.id}`} className={`border-t ${k.revoked ? "opacity-50 line-through" : ""}`}>
                <td className="p-3 font-medium">{k.name}</td>
                <td className="p-3 font-mono">{k.prefix}…</td>
                <td className="p-3">{new Date(k.created_at).toLocaleString()}</td>
                <td className="p-3">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                <td className="p-3 text-right">{!k.revoked && <Button data-testid={`admin-api-key-revoke-${k.id}`} size="sm" variant="outline" onClick={() => revoke(k.id)} className="border-red-300 text-red-700"><Trash2 className="w-4 h-4" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
